import { useRef, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import { uploadPaymentProof } from "@/lib/commercialLicenseRequestsApi";

const ALLOWED_PAYMENT_PROOF_EXT = /\.(pdf|png|jpe?g|docx?)$/i;
const MAX_PAYMENT_PROOF_BYTES = 5 * 1024 * 1024;

export function validatePaymentProofFile(file) {
  if (!file) return "Seleccione un archivo.";
  const name = String(file.name || "").toLowerCase();
  if (!ALLOWED_PAYMENT_PROOF_EXT.test(name)) {
    return "Tipo de archivo no permitido. Use PDF, PNG, JPG, JPEG, DOC o DOCX.";
  }
  if (file.size > MAX_PAYMENT_PROOF_BYTES) {
    return "El comprobante supera el tamaño máximo de 5 MB.";
  }
  return "";
}

/**
 * Comprobante de pago opcional en solicitud de licencia (comprador).
 */
export default function LicensePaymentProofSection({
  form,
  setForm,
  disabled = false,
  showPdfManualHint = false,
  testId = "license-payment-proof",
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const handlePick = () => {
    if (disabled || uploading) return;
    inputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const validationError = validatePaymentProofFile(file);
    if (validationError) {
      setUploadError(validationError);
      return;
    }
    setUploadError("");
    setUploading(true);
    try {
      const result = await uploadPaymentProof(file);
      setForm((prev) => ({
        ...prev,
        payment_proof_file_id: result.payment_proof_file_id || "",
        payment_proof_filename: result.payment_proof_filename || file.name,
      }));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setUploadError(typeof detail === "string" ? detail : "No se pudo subir el comprobante.");
    } finally {
      setUploading(false);
    }
  };

  const clearFile = () => {
    setForm((prev) => ({
      ...prev,
      payment_proof_file_id: "",
      payment_proof_filename: "",
    }));
    setUploadError("");
  };

  return (
    <section
      className="rounded-sm border border-slate-200 bg-slate-50/40 p-3 space-y-2"
      data-testid={testId}
    >
      <h5 className="text-xs font-semibold text-[#0F2D5C] m-0">Comprobante de pago</h5>
      <p className="text-[11px] text-slate-600 m-0">
        Si ya realizaste el pago, anexa aquí tu comprobante para que BNT pueda validarlo. Si aún no has pagado,
        puedes enviar la solicitud sin comprobante y BNT te contactará.
      </p>
      {showPdfManualHint ? (
        <p className="text-[10px] text-slate-500 m-0 italic">
          Si envía la solicitud por correo o WhatsApp, también puede incluir su comprobante de pago en ese envío.
        </p>
      ) : null}
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,application/pdf,image/png,image/jpeg,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="hidden"
        onChange={handleFileChange}
        data-testid={`${testId}-input`}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 text-xs"
          disabled={disabled || uploading}
          onClick={handlePick}
          data-testid={`${testId}-upload-btn`}
        >
          {uploading ? (
            <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
          ) : (
            <Upload className="w-3.5 h-3.5 mr-1" />
          )}
          Subir comprobante
        </Button>
        {form.payment_proof_filename ? (
          <>
            <span className="text-xs text-slate-700 truncate max-w-[220px]" data-testid={`${testId}-filename`}>
              {form.payment_proof_filename}
            </span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8 text-xs"
              disabled={disabled || uploading}
              onClick={clearFile}
              data-testid={`${testId}-clear-btn`}
            >
              Quitar
            </Button>
          </>
        ) : (
          <span className="text-xs text-slate-400">Sin archivo seleccionado</span>
        )}
      </div>
      {uploadError ? (
        <p className="text-xs text-red-700 m-0" data-testid={`${testId}-error`}>
          {uploadError}
        </p>
      ) : null}
      <div>
        <Label className="text-xs">Comentario del pago (opcional)</Label>
        <SpellcheckTextarea
          value={form.payment_comment || ""}
          onChange={(e) => setForm((f) => ({ ...f, payment_comment: e.target.value }))}
          disabled={disabled}
          rows={2}
          placeholder="Banco, fecha, monto, referencia o aclaración"
          className="mt-1 rounded-sm text-xs resize-none"
          testId={`${testId}-comment`}
        />
      </div>
    </section>
  );
}
